// ============================================================
// ChatComposer (v8.363.4) - the one-line box docked under the log.
//
// ALWAYS REACHABLE: it sits below the log's edge so it shows even when
// the curtain is rolled up, and ` or / over the map puts the caret in it
// (utils/composerFocus). ARIA combobox: the input keeps focus while the
// popup lists commands, people or groups for the word under the caret;
// Up/Down move, Tab or Enter take, Esc closes the popup first and then
// hands focus back to the map. Every key is handled here and STOPPED:
// the app's window hotkeys (Space attacks, T resets, letters everywhere)
// must never see a keystroke meant for the vox.
// ============================================================
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import {
  completions,
  type Completion,
  type Conversation,
  type ConversationKey,
  type Person,
  type ChatChannel,
  type SpeakAs,
} from '../engine/chat';
import { registerComposer } from '../utils/composerFocus';
import { composerKeyAction, type ComposerKeyAction } from '../utils/composerKeys';
import { liveryStyle } from '../utils/liveryStyle';
import type { Livery } from '../engine/roster/team';
import clsx from '../utils/clsx';
import styles from '../styles/CombatLogPanel.module.css';

interface Props {
  active: ConversationKey;
  conversations: Conversation[];
  people: Person[];
  channels: ChatChannel[];
  me: string;
  actor: SpeakAs | null;
  inCharacter: boolean;
  onToggleInCharacter: () => void;
  onSend: (text: string) => Promise<boolean>;
  error: string | null;
  onClearError: () => void;
  /** Where focus goes on Esc - the map. */
  onFocusMap?: () => void;
}

type Setter<T> = (value: T) => void;

/** ` or / over the map lands here (utils/composerFocus), optionally starting the line with a prefix. */
function useComposerRegistration(
  input: RefObject<HTMLInputElement | null>,
  setText: Setter<string>,
  setCaret: Setter<number>,
  setOpen: Setter<boolean>,
) {
  useEffect(() => {
    registerComposer((prefix = '') => {
      const el = input.current;
      if (!el) return;
      if (prefix) {
        setText(prefix);
        setCaret(prefix.length);
        setOpen(true);
      }
      el.focus();
      requestAnimationFrame(() => el.setSelectionRange(el.value.length, el.value.length));
    });
    return () => registerComposer(null);
    // Every dependency is stable (a ref and three state setters), so this registers once.
  }, [input, setText, setCaret, setOpen]);
}

/** The line, the caret, the popup and its highlight - and taking a completion into the line. */
function useComposerLine(input: RefObject<HTMLInputElement | null>, lists: Pick<Props, 'people' | 'channels' | 'me'>) {
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  useComposerRegistration(input, setText, setCaret, setOpen);
  const { people, channels, me } = lists;
  const options = useMemo<Completion[]>(
    () => (open ? completions(text, caret, { people, channels, me }) : []),
    [open, text, caret, people, channels, me],
  );
  useEffect(() => setHighlight(0), [options.length]);

  const take = useCallback(
    (c: Completion) => {
      const before = text.slice(0, caret);
      const word = before.match(/(\S*)$/)?.[1] ?? '';
      const next = before.slice(0, before.length - word.length) + c.insert + text.slice(caret);
      setText(next);
      const pos = before.length - word.length + c.insert.length;
      setCaret(pos);
      setOpen(true);
      requestAnimationFrame(() => input.current?.setSelectionRange(pos, pos));
    },
    [text, caret, input],
  );
  return { text, setText, caret, setCaret, open, setOpen, highlight, setHighlight, options, take };
}

type Line = ReturnType<typeof useComposerLine>;

/** The composer's keys, per `composerKeyAction`. Every key is STOPPED first: the window's hotkeys never see vox typing. */
function useComposerKeys(
  line: Line,
  input: RefObject<HTMLInputElement | null>,
  submit: () => Promise<void>,
  onFocusMap?: () => void,
) {
  const { open, options, highlight, text } = line;
  const run: Record<ComposerKeyAction, () => void> = {
    close: () => line.setOpen(false),
    leave: () => {
      input.current?.blur();
      onFocusMap?.();
    },
    next: () => line.setHighlight((h) => (h + 1) % options.length),
    prev: () => line.setHighlight((h) => (h - 1 + options.length) % options.length),
    take: () => line.take(options[highlight]),
    submit: () => void submit(),
  };
  return (e: KeyboardEvent<HTMLDivElement>) => {
    // THE FENCE: nothing typed here reaches the window's hotkeys.
    e.stopPropagation();
    const action = composerKeyAction(e.key, {
      open,
      options: options.length,
      highlightIsCommand: options[highlight]?.kind === 'command',
      words: text.trim().split(/\s+/).length,
    });
    if (!action) return;
    e.preventDefault();
    run[action]();
  };
}

/** In character (as the actor, in their livery) or out of it. */
function InCharacterToggle({
  actor,
  inCharacter,
  onToggle,
}: {
  actor: SpeakAs | null;
  inCharacter: boolean;
  onToggle: () => void;
}) {
  const speaking = inCharacter && !!actor;
  let title = 'Acting as nobody';
  if (actor) {
    title = inCharacter
      ? `Speaking as ${actor.name} - click to speak as yourself`
      : `Speaking as yourself - click to speak as ${actor.name}`;
  }
  return (
    <button
      type="button"
      className={clsx(styles.chatIcToggle, speaking && styles.chatIcOn)}
      style={speaking ? liveryStyle(actor!.livery as Livery | null) : undefined}
      onClick={onToggle}
      title={title}
      aria-pressed={speaking}
      data-testid="chat-ic-toggle"
    >
      {speaking ? actor!.name : 'OOC'}
    </button>
  );
}

const LIST_ID = 'chat-completions';

function CompletionList({
  options,
  highlight,
  onTake,
}: {
  options: Completion[];
  highlight: number;
  onTake: (c: Completion) => void;
}) {
  return (
    <ul className={styles.chatCompletions} role="listbox" id={LIST_ID} data-testid="chat-completions">
      {options.map((o, i) => (
        <li
          key={o.kind + o.insert}
          id={`${LIST_ID}-${i}`}
          role="option"
          aria-selected={i === highlight}
          className={clsx(styles.chatCompletion, i === highlight && styles.chatCompletionActive)}
          onMouseDown={(e) => {
            e.preventDefault();
            onTake(o);
          }}
        >
          {o.label}
        </li>
      ))}
    </ul>
  );
}

/** The ARIA combobox itself: the input keeps focus while the popup lists completions. */
function ComposerInput({
  input,
  line,
  label,
  error,
  onClearError,
}: {
  input: RefObject<HTMLInputElement | null>;
  line: Line;
  label: string;
  error: string | null;
  onClearError: () => void;
}) {
  const { setText, setCaret, setOpen } = line;
  const listing = line.open && line.options.length > 0;
  return (
    <input
      ref={input}
      className={styles.chatInput}
      value={line.text}
      placeholder={`Vox… (${label})`}
      role="combobox"
      aria-expanded={listing}
      aria-controls={LIST_ID}
      aria-autocomplete="list"
      aria-activedescendant={listing ? `${LIST_ID}-${line.highlight}` : undefined}
      aria-label="Vox"
      data-testid="chat-input"
      maxLength={4000}
      onChange={(e) => {
        setText(e.target.value);
        setCaret(e.target.selectionStart ?? e.target.value.length);
        setOpen(true);
        if (error) onClearError();
      }}
      onSelect={(e) => setCaret((e.target as HTMLInputElement).selectionStart ?? 0)}
      onBlur={() => setTimeout(() => setOpen(false), 120)}
      onFocus={() => setOpen(true)}
    />
  );
}

export default function ChatComposer(props: Props) {
  const { active, conversations, error, onClearError, onSend } = props;
  const input = useRef<HTMLInputElement>(null);
  const line = useComposerLine(input, props);
  const { text, setText, open, setOpen, options, highlight } = line;

  const submit = useCallback(async () => {
    const said = text;
    if (!said.trim()) return;
    if (await onSend(said)) setText('');
    setOpen(false);
  }, [text, onSend, setText, setOpen]);
  const onKeyDown = useComposerKeys(line, input, submit, props.onFocusMap);

  const label = conversations.find((c) => c.key === active)?.label ?? 'Vox — all';
  const listing = open && options.length > 0;
  return (
    <div
      className={styles.chatComposer}
      // A key fence, not a control: it keeps typing out of the window hotkeys.
      role="presentation"
      onKeyDown={onKeyDown}
      onKeyUp={(e) => e.stopPropagation()}
      data-testid="chat-composer"
    >
      <InCharacterToggle actor={props.actor} inCharacter={props.inCharacter} onToggle={props.onToggleInCharacter} />
      <div className={styles.chatInputWrap}>
        <ComposerInput input={input} line={line} label={label} error={error} onClearError={onClearError} />
        {listing && <CompletionList options={options} highlight={highlight} onTake={line.take} />}
        {error && (
          <div className={styles.chatError} role="alert" data-testid="chat-error">
            {error}
          </div>
        )}
      </div>
      <button
        type="button"
        className={styles.chatSend}
        onClick={() => void submit()}
        data-testid="chat-send"
        aria-label="Send"
      >
        ➤
      </button>
    </div>
  );
}
